import { useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useFormContext, useWatch } from 'react-hook-form';
import FilterIcon from 'components/filters/FilterIcon';
import usePrevious from 'hooks/usePrevious';
import { isDeepEqual } from 'utils/objectUtils';
import { GetPropertyOptionsFilterProps } from 'utils/property/propertyUtils';
import PropertyMultiSelectField from '../PropertyMultiSelectField/PropertyMultiSelectField';
import StatusSelectField from '../StatusSelectField';
import TagsMultiSelectField from '../TagsMultiSelectField';
import { PropertyMultiSelectFieldProps } from '../PropertyMultiSelectField/PropertyMultiSelectField.types';
import { FilterContainerStyled } from './PropertyMultiSelectFilterField.styles';

interface PropertyMultiSelectWithFiltersProps
  extends GetPropertyOptionsFilterProps {
  name: string;
  graphQlFilters?: PropertyMultiSelectFieldProps['graphQlFilters'];
  filterPropertyBy?: PropertyMultiSelectFieldProps['filterPropertyBy'];
  filtersColXs?: number;
}

const PropertyMultiSelectWithFilters = ({
  name,
  graphQlFilters,
  filterPropertyBy,
  filtersColXs,
  ...getPropertyOptionsFilterProps
}: PropertyMultiSelectWithFiltersProps) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const {
    setValue,
    formState: { defaultValues },
  } = useFormContext();
  const statusFieldName = `${name}Status`;
  const tagsFieldName = `${name}Tags`;
  const propertiesFieldName = `${name}Properties`;
  // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
  const propertiesDefaultValue = defaultValues[propertiesFieldName];
  const status = useWatch({ name: statusFieldName });
  const tags = useWatch({ name: tagsFieldName }) ?? [];
  const previousStatus = usePrevious(status);
  const previousTags = usePrevious(tags);
  const handleFilterClick = () => setIsCollapsed((prev) => !prev);

  // Clear selected properties when changing filters, so that it doesn't keep
  // properties that no longer applies to the current filters
  if (
    (status !== undefined && previousStatus !== status) ||
    (!isDeepEqual(tags, []) && !isDeepEqual(previousTags, tags))
  ) {
    setValue(propertiesFieldName, propertiesDefaultValue ?? []);
  }

  return (
    <>
      <FilterIcon
        isFilterOpen={!isCollapsed}
        // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        filterCounter={undefined}
        onClick={handleFilterClick}
      />

      <FilterContainerStyled
        isCollapsed={isCollapsed}
        className="property-multi-select-filter"
      >
        <Row>
          <Col xs={filtersColXs}>
            <StatusSelectField
              name={statusFieldName}
              colSmLabel={3}
              colSmInput={9}
            />
          </Col>
          <Col xs={filtersColXs}>
            <TagsMultiSelectField
              name={tagsFieldName}
              colSmLabel={3}
              colSmInput={9}
            />
          </Col>
        </Row>
      </FilterContainerStyled>

      <PropertyMultiSelectField
        name={propertiesFieldName}
        colSmInput={12}
        graphQlFilters={{ tags, status, ...graphQlFilters }}
        filterPropertyBy={filterPropertyBy}
        isSelectAllEnabled
        {...getPropertyOptionsFilterProps}
      />
    </>
  );
};

PropertyMultiSelectWithFilters.defaultProps = {
  graphQlFilters: undefined,
  filterPropertyBy: undefined,
  filtersColXs: 6,
};

export default PropertyMultiSelectWithFilters;
